import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { ExerciseFormDialog } from '@/components/exercises/ExerciseFormDialog'
import {
  exercisesApi,
  type Exercise,
  type ExerciseLevel,
  type ExerciseSource,
  type ExerciseStatus,
  type ExerciseType,
} from '@/api/exercises'
import { useSubscriptionParameterFresh } from '@/hooks/useSubscriptionParameterFresh'
import { formatDateDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'

const TYPES: ExerciseType[] = [
  'drag_gaps',
  'cloze',
  'dropdown_gaps',
  'choice',
  'true_false',
  'matching',
  'sentence_builder',
  'ordering',
  'grouping',
  'error_correction',
  'translation',
  'picture',
  'crossword',
  'listening',
  'open_answer',
  'oral_answer',
  'dictation',
  'card_test',
]

const STATUSES: ExerciseStatus[] = ['draft', 'ready', 'archived']

const LEVELS: ExerciseLevel[] = ['low', 'medium', 'high']

const SOURCES: ExerciseSource[] = ['own', 'system']

/** Пауза перед запросом поиска: без неё список дёргался на каждую букву */
const SEARCH_DEBOUNCE_MS = 350

/** Строк в скелетоне: столько же, сколько обычно влезает на экран без прокрутки */
const SKELETON_ROWS = [1, 2, 3, 4, 5]

/**
 * Банк заданий учителя — единый каталог вместо прежней страницы диктантов.
 *
 * Задание живёт здесь, а в программу и в домашку попадает ссылкой: одно
 * задание может стоять в нескольких темах сразу, и правка доезжает до всех.
 * Диктант — такой же тип задания, поэтому отдельного раздела ему больше не
 * нужно: он находится фильтром по типу.
 *
 * Страница рабочая, а не витрина: завести, поправить, скопировать себе и
 * убрать в архив задание можно прямо здесь — раньше для этого приходилось
 * идти в программу и искать тему, где задание стоит.
 */
export function ExerciseBankPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const premiumEnabled = useSubscriptionParameterFresh('premium_exercises_enabled')
  // Второй параметр читаем из уже обновлённой карты (enabled=false): банк живёт
  // без модуля программ, и лишний запрос за той же картой здесь не нужен
  const programsEnabled = useSubscriptionParameterFresh('learning_programs_enabled', false)

  const [items, setItems] = useState<Exercise[]>([])
  const [total, setTotal] = useState(0)
  const [type, setType] = useState<ExerciseType | ''>('')
  const [level, setLevel] = useState<ExerciseLevel | ''>('')
  const [status, setStatus] = useState<ExerciseStatus | ''>('')
  const [source, setSource] = useState<ExerciseSource | ''>('')
  // Введённое в поле и то, что ушло в запрос: между ними дебаунс
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(25)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuTarget, setMenuTarget] = useState<Exercise | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Exercise | null>(null)
  const [formReadOnly, setFormReadOnly] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<Exercise | null>(null)
  const [archiving, setArchiving] = useState(false)

  // Номер запроса: фильтры переключают быстро, и без него в списке оказывался
  // тот ответ, что пришёл последним, а не тот, что соответствует фильтру
  const requestRef = useRef(0)
  // Тот же счётчик для дозагрузки задания в конструктор
  const detailsRequestRef = useRef(0)

  /**
   * Сколько фильтров сузило выдачу — для счётчика на свёрнутой кнопке.
   *
   * Поиск не считаем: его поле стоит в тулбаре и видно всегда, а бейдж должен
   * отвечать только за то, что спрятано под Collapse. Иначе учитель видел бы
   * «1» при пустой панели и искал бы там несуществующий фильтр.
   */
  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (type !== '') count += 1
    if (level !== '') count += 1
    if (source !== '') count += 1
    if (status !== '') count += 1

    return count
  }, [level, source, status, type])

  const load = useCallback(async () => {
    const request = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      const result = await exercisesApi.listBank({
        type,
        level,
        status,
        source,
        q: search,
        // API считает страницы с единицы, MUI — с нуля
        page: page + 1,
        perPage,
      })

      if (request !== requestRef.current) return

      setItems(result.items)
      setTotal(result.total)
    } catch (err) {
      if (request === requestRef.current) setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }, [type, level, status, source, search, page, perPage, t])

  // Запрос уходит через паузу после последнего нажатия: без дебаунса поиск
  // бил в API на каждую букву, и в списке оседал ответ по обрывку слова
  useEffect(() => {
    const next = searchInput.trim()
    // Страницу сбрасываем только при реальной смене запроса: иначе правка
    // пробела в поле выкидывала бы учителя со второй страницы на первую
    if (next === search) return

    const timer = window.setTimeout(() => {
      setSearch(next)
      setPage(0)
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [searchInput, search])

  useEffect(() => {
    void load()
  }, [load])

  // Страница за пределами выдачи: последнее задание с неё убрали в архив или
  // сузили фильтр. MUI на таком page ругается и рисует пустой диапазон, поэтому
  // возвращаемся на последнюю существующую страницу сами
  useEffect(() => {
    if (loading || page === 0 || page * perPage < total) return

    setPage(total === 0 ? 0 : Math.ceil(total / perPage) - 1)
  }, [loading, page, perPage, total])

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuTarget(null)
  }

  /**
   * Открыть конструктор. Существующее задание догружаем по id: в списке
   * эталоны вычищены санитайзером, и сохранение формы, заполненной из строки
   * таблицы, стёрло бы правильные ответы.
   */
  const openForm = async (exercise: Exercise | null, readOnly: boolean) => {
    closeMenu()
    // Номер запроса: пока грузится одно задание, учитель успевает закрыть
    // ожидание и открыть другое. Без сверки поздний ответ подменял бы уже
    // открытую форму чужим заданием — и сохранение уходило бы не туда
    const request = ++detailsRequestRef.current

    setFormReadOnly(readOnly)

    if (!exercise) {
      setEditing(null)
      setFormOpen(true)
      return
    }

    setDetailsLoading(true)
    setError(null)
    try {
      const full = await exercisesApi.get(exercise.id)
      if (request !== detailsRequestRef.current) return

      setEditing(full)
      setFormOpen(true)
    } catch (err) {
      if (request === detailsRequestRef.current) setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (request === detailsRequestRef.current) setDetailsLoading(false)
    }
  }

  const duplicate = async (exercise: Exercise) => {
    closeMenu()
    setError(null)
    try {
      // Без темы: копия ложится в банк и ставится в программу отдельным шагом
      const copy = await exercisesApi.duplicate(exercise.id)
      setNotice(t('exercises.bank.duplicated', 'Копия «{{title}}» создана в банке', { title: copy.title }))
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  /**
   * Возврат из архива: задание снова видно в банке, но по местам, откуда его
   * сняли, не расставляется — архивация их отвязала, и восстановить их
   * автоматически нельзя.
   */
  const restore = async (exercise: Exercise) => {
    closeMenu()
    setError(null)
    try {
      await exercisesApi.update(exercise.id, { status: 'ready' })
      setNotice(t('exercises.bank.restored', 'Задание «{{title}}» вернулось в банк', { title: exercise.title }))
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  const archive = async () => {
    if (!archiveTarget) return

    setArchiving(true)
    setError(null)
    try {
      const usage = await exercisesApi.archive(archiveTarget.id)

      // Числа приходят из ответа: до архивации клиент не знает, где задание
      // стояло, — оно могло быть в чужой теме и в уже выданной домашке.
      // Домашки при этом не переписываются: пункт остаётся, но начать по нему
      // попытку нельзя — текст обязан говорить именно это
      setNotice(
        programsEnabled
          ? t(
              'exercises.bank.archivedWithUsage',
              'Задание в архиве. Снято из программ: {{catalog}}, из назначенных тем: {{assignments}}. Останется в домашках ({{homework}}), но начать его больше нельзя.',
              {
                catalog: usage.catalog,
                assignments: usage.assignments,
                homework: usage.homework_items,
              },
            )
          : t(
              'exercises.bank.archivedHomeworkOnly',
              'Задание в архиве. Останется в домашках ({{homework}}), но начать его больше нельзя.',
              { homework: usage.homework_items },
            ),
      )
      setArchiveTarget(null)
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setArchiving(false)
    }
  }

  // Отбор задан хоть чем-то: `search`, а не `searchInput`, — пока дебаунс не
  // отпустил, список ещё отвечает прежнему запросу, и подпись под ним обязана
  // говорить про него же
  const filtersApplied =
    search !== '' || type !== '' || level !== '' || status !== '' || source !== ''

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, exercise: Exercise) => {
    setMenuAnchor(event.currentTarget)
    setMenuTarget(exercise)
  }

  /**
   * Название-ссылка. Вид повторяем с ProgramsPage (полужирный текст обычного
   * цвета), подчёркивание по наведению — чтобы было видно, что это ссылка.
   * `component="button"` вместо строки в ячейке: открывается диалог, а не
   * маршрут, и клавиатуре нужен настоящий фокусируемый элемент.
   */
  const titleLinkSx = { fontSize: 14, fontWeight: 600, textAlign: 'left' as const, cursor: 'pointer' }

  /** Шапка таблицы одна и для скелетона, и для данных: разъехаться они не должны */
  const tableHead = (
    <TableHead>
      <TableRow>
        <TableCell>{t('exercises.bank.name')}</TableCell>
        <TableCell>{t('exercises.bank.type')}</TableCell>
        <TableCell>{t('exercises.bank.level', 'Сложность')}</TableCell>
        <TableCell>{t('exercises.bank.status')}</TableCell>
        <TableCell>{t('exercises.bank.updatedAt')}</TableCell>
        <TableCell align="right">{t('exercises.bank.actions', 'Действия')}</TableCell>
      </TableRow>
    </TableHead>
  )

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs items={[{ label: t('exercises.bank.title') }]} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('exercises.bank.title')}
          </Typography>
          <PageHelpPopover pageKey="exercise-bank" />
        </Box>

        {/* Банк работает и без модуля программ — но обещать расстановку по
            темам, которой у учителя нет, страница не должна */}
        {!programsEnabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t(
              'exercises.bank.programsDisabledHint',
              'Модуль «Программы обучения» не подключён: задания можно создавать, править и выдавать в домашках, а расставить их по темам программы — нет.',
            )}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('exercises.bank.searchPlaceholder', 'Поиск по названию')}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 240, flex: isMobile ? 1 : undefined }}
          />
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((open) => !open)}
            label={t('exercises.bank.filtersButton', 'Фильтры')}
          />
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ height: 40, whiteSpace: 'nowrap' }}
              onClick={() => void openForm(null, false)}
            >
              {t('exercises.bank.create', 'Создать задание')}
            </Button>
          </Box>
        </Box>

        {/* Все четыре фильтра описаны в OpenAPI у GET /exercises — прятать их в
            Collapse можно без риска отправить в API параметр, которого нет */}
        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              select
              size="small"
              label={t('exercises.bank.type')}
              value={type}
              onChange={(event) => {
                setType(event.target.value as ExerciseType | '')
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('exercises.bank.anyType')}</MenuItem>
              {TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`exercises.types.${value}.name`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('exercises.bank.level', 'Сложность')}
              value={level}
              onChange={(event) => {
                setLevel(event.target.value as ExerciseLevel | '')
                setPage(0)
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('exercises.bank.anyLevel', 'Любая сложность')}</MenuItem>
              {LEVELS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`exercises.levels.${value}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('exercises.bank.source', 'Источник')}
              value={source}
              onChange={(event) => {
                setSource(event.target.value as ExerciseSource | '')
                setPage(0)
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('exercises.bank.anySource', 'Все задания')}</MenuItem>
              {SOURCES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`exercises.bank.sources.${value}`, value === 'own' ? 'Мои' : 'EasyA')}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label={t('exercises.bank.status')}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as ExerciseStatus | '')
                setPage(0)
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('exercises.bank.activeOnly')}</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`exercises.exerciseStatus.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Collapse>

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {SKELETON_ROWS.map((row) => (
                <Paper key={row}>
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                    <Skeleton variant="text" width="30%" />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                {tableHead}
                <TableBody>
                  {SKELETON_ROWS.map((row) => (
                    <TableRow key={row}>
                      <TableCell>
                        <Skeleton variant="text" width="70%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={90} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={60} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="circular" width={32} height={32} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : items.length === 0 ? (
          <Paper>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              {/* Пустой ответ по фильтрам — не пустой банк: учитель с двумя
                  сотнями заданий читал «Заданий пока нет» как их потерю, а
                  «Создать первое задание» под этим только подтверждало испуг.
                  Опустевшая страница — тоже не пустой банк: задания есть, но
                  на предыдущих страницах, и звать заводить первое здесь тем
                  более нельзя */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: filtersApplied || page > 0 ? 0 : 2 }}
              >
                {page > 0
                  ? t('exercises.bank.emptyPage', 'На этой странице заданий не осталось')
                  : filtersApplied
                    ? t('exercises.bank.emptyFiltered')
                    : t('exercises.bank.empty')}
              </Typography>
              {!filtersApplied && page === 0 && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ height: 40 }}
                  onClick={() => void openForm(null, false)}
                >
                  {t('exercises.bank.createFirst')}
                </Button>
              )}
            </Box>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((exercise) => (
              <Paper key={exercise.id}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* На карточке название — тот же вход в просмотр, что и в
                        таблице: на телефоне меню «три точки» ещё мельче */}
                    <MuiLink
                      component="button"
                      type="button"
                      color="inherit"
                      underline="hover"
                      sx={titleLinkSx}
                      onClick={() => void openForm(exercise, true)}
                    >
                      {exercise.title}
                    </MuiLink>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1 }}
                    >
                      <Chip size="small" label={t(`exercises.types.${exercise.type}.name`)} />
                      <Chip
                        size="small"
                        variant="outlined"
                        color={exercise.status === 'ready' ? 'success' : 'default'}
                        label={t(`exercises.exerciseStatus.${exercise.status}`)}
                      />
                      {exercise.is_system && (
                        <Chip size="small" color="secondary" label={t('exercises.bank.systemBadge')} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t('exercises.bank.level', 'Сложность')}: {t(`exercises.levels.${exercise.level}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('exercises.bank.updatedAt')}:{' '}
                      {exercise.content_updated_at ? formatDateDMY(exercise.content_updated_at) : '—'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={t('exercises.bank.actions', 'Действия')}
                    onClick={(event) => openRowMenu(event, exercise)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              {tableHead}
              <TableBody>
                {items.map((exercise) => (
                  <TableRow key={exercise.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {/* Клик по названию открывает просмотр, а не редактор:
                            посмотреть задание хотят чаще, чем править, и
                            случайная правка чужой формулировки дороже */}
                        <MuiLink
                          component="button"
                          type="button"
                          color="inherit"
                          underline="hover"
                          sx={titleLinkSx}
                          onClick={() => void openForm(exercise, true)}
                        >
                          {exercise.title}
                        </MuiLink>
                        {exercise.is_system && (
                          <Chip size="small" color="secondary" label={t('exercises.bank.systemBadge')} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`exercises.types.${exercise.type}.name`)} />
                    </TableCell>
                    <TableCell>{t(`exercises.levels.${exercise.level}`)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={exercise.status === 'ready' ? 'success' : 'default'}
                        label={t(`exercises.exerciseStatus.${exercise.status}`)}
                      />
                    </TableCell>
                    <TableCell>
                      {exercise.content_updated_at ? formatDateDMY(exercise.content_updated_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={t('exercises.bank.actions', 'Действия')}
                        onClick={(event) => openRowMenu(event, exercise)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Пагинация вне ветки с данными: со страницы, которая опустела
            (задания заархивировали или отфильтровали), учитель иначе не мог
            вернуться назад — контрол исчезал вместе со списком */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, value) => setPage(value)}
            rowsPerPage={perPage}
            rowsPerPageOptions={[25, 50, 100]}
            onRowsPerPageChange={(event) => {
              setPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            labelRowsPerPage={isMobile ? '' : t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {/* Задание витрины правит платформа: учителю оно доступно только на
              чтение, а работать с ним он начинает со своей копии */}
          <MenuItem onClick={() => menuTarget && void openForm(menuTarget, menuTarget.is_system)}>
            <Stack direction="row" spacing={1} alignItems="center">
              {menuTarget?.is_system ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
              <span>
                {menuTarget?.is_system
                  ? t('exercises.bank.view', 'Посмотреть')
                  : t('exercises.bank.edit', 'Редактировать')}
              </span>
            </Stack>
          </MenuItem>

          <MenuItem onClick={() => menuTarget && void duplicate(menuTarget)}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ContentCopyIcon fontSize="small" />
              <span>{t('exercises.bank.duplicate', 'Дублировать себе')}</span>
            </Stack>
          </MenuItem>

          {/* Архивное задание возвращают тем же меню: иначе случайная архивация
              необратима, и учитель теряет задание вместе со всей его историей */}
          {menuTarget?.status === 'archived' && !menuTarget.is_system ? (
            <MenuItem onClick={() => menuTarget && void restore(menuTarget)}>
              <Stack direction="row" spacing={1} alignItems="center">
                <UnarchiveIcon fontSize="small" />
                <span>{t('exercises.bank.restore', 'Вернуть из архива')}</span>
              </Stack>
            </MenuItem>
          ) : (
            <MenuItem
              disabled={menuTarget?.is_system}
              onClick={() => {
                setArchiveTarget(menuTarget)
                closeMenu()
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <ArchiveIcon fontSize="small" />
                <span>{t('exercises.bank.archive', 'Архивировать')}</span>
              </Stack>
            </MenuItem>
          )}
        </Menu>

        <Dialog open={archiveTarget !== null} onClose={() => setArchiveTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('exercises.bank.archiveTitle', 'Убрать задание в архив?')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {programsEnabled
                ? t(
                    'exercises.bank.archiveConfirm',
                    'Задание «{{title}}» снимется с тем программ и назначенных тем учеников. В уже выданных домашках оно останется, но начать его будет нельзя. Сколько это мест — покажем после архивации. Возврат из архива вернёт задание в банк, но не расставит его обратно по темам.',
                    { title: archiveTarget?.title ?? '' },
                  )
                : t(
                    'exercises.bank.archiveConfirmHomeworkOnly',
                    'Задание «{{title}}» уйдёт в архив. В уже выданных домашках оно останется, но начать его будет нельзя.',
                    { title: archiveTarget?.title ?? '' },
                  )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveTarget(null)}>{t('common.cancel')}</Button>
            <Button color="error" variant="contained" disabled={archiving} onClick={() => void archive()}>
              {archiving ? t('common.loading') : t('exercises.bank.archive', 'Архивировать')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Догрузка задания видна сразу: между кликом и открытием конструктора
            лежит запрос, и без этого окна страница выглядела бы «не нажалось» */}
        <Dialog open={detailsLoading} maxWidth="md" fullWidth>
          <DialogTitle>{t('common.loading')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          </DialogContent>
        </Dialog>

        <ExerciseFormDialog
          open={formOpen}
          themeId={null}
          exercise={editing}
          premiumEnabled={premiumEnabled}
          readOnly={formReadOnly}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            void load()
          }}
        />

        <Snackbar
          open={notice !== null}
          autoHideDuration={6000}
          onClose={() => setNotice(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  )
}
